import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ArrowRight, TrendingUp, Wallet, PieChart, Sparkles } from "lucide-react";

export default function Landing() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [showLogin, setShowLogin] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleGoogleLogin = async (response) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/session", {
        id_token: response.credential,
      });
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal masuk dengan Google");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  React.useEffect(() => {
    let interval;
    if (showLogin) {
      const initGoogle = () => {
        /* global google */
        if (typeof google !== "undefined") {
          try {
            google.accounts.id.initialize({
              client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || "555958806309-49hvskfre45dst0p00ed5h9i9pejlvfb.apps.googleusercontent.com",
              callback: handleGoogleLogin,
            });
            const btnEl = document.getElementById("google-signin-button");
            if (btnEl) {
              google.accounts.id.renderButton(btnEl, {
                theme: "outline",
                size: "large",
                width: 320,
              });
              clearInterval(interval);
            }
          } catch (err) {
            console.error("Google Initialisation error:", err);
          }
        }
      };

      initGoogle();
      interval = setInterval(initGoogle, 500);
    }
    return () => clearInterval(interval);
  }, [showLogin]);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Grid backdrop */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <nav className="relative z-10 h-16 border-b border-border flex items-center px-6 md:px-10 justify-between">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 bg-foreground text-background flex items-center justify-center font-heading font-bold text-sm">A</div>
          <span className="font-heading text-lg font-medium tracking-tight">Aliran</span>
        </div>
        <Button onClick={() => setShowLogin(true)} data-testid="landing-login-btn" className="bg-foreground text-background hover:bg-foreground/90">
          Masuk ke Akun <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </nav>

      <section className="relative z-10 px-6 md:px-10 pt-20 pb-24 max-w-6xl">
        <div className="label-caps mb-6">Cash flow • IDR • Sejak 2026</div>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.95] max-w-4xl">
          Uang mengalir. <br/>
          <span className="text-muted-foreground">Kendali di tanganmu.</span>
        </h1>
        <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          Aliran adalah dashboard cash flow personal untuk mereka yang serius menata keuangan.
          Catat transaksi, susun tujuan, pantau portofolio, dan dapatkan insight AI — semua dalam satu tempat.
        </p>
        <div className="mt-10 flex gap-3">
          <Button
            size="lg"
            onClick={() => setShowLogin(true)}
            data-testid="landing-cta-btn"
            className="bg-foreground text-background hover:bg-foreground/90 h-12 px-6"
          >
            Mulai Sekarang <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <section className="relative z-10 px-6 md:px-10 pb-24 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Wallet, title: "Multi-Akun", desc: "Kelola tabungan, e-wallet, dan kartu kredit di satu tempat." },
            { icon: TrendingUp, title: "Cash Flow Nyata", desc: "Lihat aliran uang bulanan dengan grafik yang jujur." },
            { icon: PieChart, title: "Portofolio", desc: "Pantau investasi saham, kripto, dan reksa dana." },
            { icon: Sparkles, title: "AI Insight", desc: "Tanya AI tentang kebiasaan belanja dan strategi menabung." },
          ].map((f) => (
            <div key={f.title} className="border border-border p-5 bg-card hover:bg-accent/40 transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(/\s/g,'-')}`}>
              <f.icon className="h-5 w-5 mb-4" />
              <h3 className="font-heading text-lg font-medium">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-card border border-border p-6 rounded-none relative shadow-xl">
            <button
              onClick={() => setShowLogin(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground text-sm font-mono"
            >
              [tutup]
            </button>
            <h2 className="font-heading text-2xl font-medium tracking-tight mb-4 text-center">Masuk ke Aliran</h2>
            
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm mb-4 text-center">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <div className="flex flex-col items-center justify-center py-4 border border-dashed border-border bg-accent/10">
                <span className="text-xs label-caps text-muted-foreground mb-3">Google Sign-In</span>
                <div id="google-signin-button" className="min-h-[40px]"></div>
                {loading && <p className="text-xs text-muted-foreground mt-2">Memproses masuk...</p>}
              </div>

            </div>
          </div>
        </div>
      )}

      <footer className="relative z-10 border-t border-border px-6 md:px-10 py-8 text-xs text-muted-foreground">
        © 2026 Aliran • Built with care for Indonesian rupiah.
      </footer>
    </div>
  );
}
